/**
 * The map — everything with a position, on one island.
 *
 * ── What it shows ────────────────────────────────────────────────────────────
 *
 * §19 of the source specification: listing locations, the destination property,
 * ferry terminals, major transport points, clustering when there are many, and
 * radius visualisation. All four layers come from the two server modules that
 * already exist — `geography.ts`'s `hh_locations` and the template's `places.ts`
 * — plus `GET /api/listings` for the one join the locations table cannot do on
 * its own (see "Why listings are fetched too", below).
 *
 * ── Clustering, with no library and no new server route ──────────────────────
 *
 * §10.3 describes `GET /api/map` returning grid cells above 120 pins in the
 * bbox. That route is not in any Phase 1 unit's route table, so it does not
 * exist yet — and the same arithmetic works here: a cluster is just a marker
 * whose label is a count, and `MapMarker` is already `{ id, lat, lng, label }`.
 * Cell size is §10.3's own `360 / 2 ** (zoom + 2)` degrees, so when the server
 * route does land the two agree by construction rather than by coincidence.
 *
 * ── Why the map is REMOUNTED to move ─────────────────────────────────────────
 *
 * `Map.tsx` treats `center` and `zoom` as first-render SEEDS — deliberately, so
 * that re-centering a map the visitor has panned is not something a stray
 * re-render can do. Passing a new `center` therefore does nothing at all, which
 * looks exactly like a broken feature. So flying somewhere means a new `key`,
 * which is what §10.3 specifies. It is a real remount: Leaflet is torn down and
 * rebuilt, which is why it happens on an explicit tap and never on a data
 * refresh.
 *
 * ── Why listings are fetched too ─────────────────────────────────────────────
 *
 * A `hh_locations` row for a listing carries `subject_id = <listing id>`, not
 * the opportunity. Tapping a pin has to open `/o/<opportunity id>`, so the
 * listing rows are read for the `location_id → opportunity_id` mapping. Sending
 * somebody to `/o/<listing id>` would resolve to `notFound`, which reads as the
 * map being broken rather than as an id mix-up.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { get } from '../lib/api'
import { useLocale, type Locale } from '../lib/i18n'
import { navigate } from '../lib/route'
import MapView, { OSM_DEV_TILES, type MapCircle, type MapMarker } from '../components/Map'
import type { LatLng } from '../lib/geo'

/* ────────────────────────────────────────────────────────────────────────────
 * Server shapes
 * ────────────────────────────────────────────────────────────────────────── */

interface LocationRow {
  id: string
  role: string
  label: string
  lat: number | null
  lng: number | null
  subject_kind: string | null
  subject_id: string | null
}

interface ListingRow {
  id: string
  opportunity_id: string | null
  title: string
  price_cents: number | null
  location_id?: string | null
  availability: string
}

interface PlaceRow {
  id: string
  name: string
  kind: string
  lat: number
  lng: number
}

/* ────────────────────────────────────────────────────────────────────────────
 * Layers
 * ────────────────────────────────────────────────────────────────────────── */

type LayerId = 'listings' | 'destination' | 'ferry' | 'transport'

const LOCATION_ROLES: Record<LayerId, string[]> = {
  listings: ['listing', 'structure_current'],
  destination: ['destination_land'],
  ferry: ['ferry_terminal', 'port'],
  transport: ['transport_origin', 'vendor_yard'],
}

/** `places.kind` values that mean the same thing as the location roles above. */
const PLACE_KINDS: Record<LayerId, string[]> = {
  listings: [],
  destination: [],
  ferry: ['ferry', 'ferry_terminal', 'port', 'terminal'],
  transport: ['transport', 'vendor', 'yard', 'hauler'],
}

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  title: 'Map',
  listings: 'Listings',
  destination: 'The land',
  ferry: 'Ferry & ports',
  transport: 'Transport',
  radius: 'Radius',
  radiusOff: 'Off',
  loading: 'Loading…',
  empty: 'Nothing with a position yet.',
  clustered: 'Tap a number to zoom in.',
  reset: 'Whole island',
  failed: 'The map data could not be loaded.',
  pins: 'places',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Mapa',
  listings: 'Anuncios',
  destination: 'El terreno',
  ferry: 'Lanchas y puertos',
  transport: 'Transporte',
  radius: 'Radio',
  radiusOff: 'Apagado',
  loading: 'Cargando…',
  empty: 'Todavía no hay nada con posición.',
  clustered: 'Toca un número para acercarte.',
  reset: 'Toda la isla',
  failed: 'No se pudieron cargar los datos del mapa.',
  pins: 'lugares',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/* ────────────────────────────────────────────────────────────────────────────
 * Geometry
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Vieques, wide.
 *
 * §10.3, and it is a privacy decision rather than an aesthetic one: every tile
 * request tells the tile host which square of the earth is being looked at, and
 * the tightest zoom is where that signal is strongest. Opening island-wide means
 * the first paint never announces the parcel.
 */
const ISLAND: LatLng = { lat: 18.12, lng: -65.44 }
const ISLAND_ZOOM = 12
const MAX_ZOOM = 18

/** Above this many pins in view, the grid takes over. §10.3's number. */
const CLUSTER_ABOVE = 120

const RADII_KM = [0, 10, 25, 50, 100]

interface Pin extends MapMarker {
  layer: LayerId
  /** Where a tap goes, when a tap goes anywhere. */
  opportunityId?: string | null
}

/** §10.3's cell size, so this and the eventual `GET /api/map` agree by construction. */
function cellSizeDeg(zoom: number): number {
  return 360 / 2 ** (zoom + 2)
}

function clusterPins(pins: Pin[], zoom: number): MapMarker[] {
  const size = cellSizeDeg(zoom)
  const cells = new Map<string, { lat: number; lng: number; count: number }>()
  for (const pin of pins) {
    const row = Math.floor(pin.lat / size)
    const col = Math.floor(pin.lng / size)
    const key = `${row}:${col}`
    const cell = cells.get(key)
    if (cell) {
      cell.lat += pin.lat
      cell.lng += pin.lng
      cell.count += 1
    } else {
      cells.set(key, { lat: pin.lat, lng: pin.lng, count: 1 })
    }
  }
  return [...cells.entries()].map(([key, cell]) => ({
    // The centroid, not the cell corner: a cluster badge sitting in the sea
    // off the corner of a grid square is how a cluster stops looking like the
    // things it contains.
    id: `C:${key}`,
    lat: cell.lat / cell.count,
    lng: cell.lng / cell.count,
    label: String(cell.count),
  }))
}

/* ────────────────────────────────────────────────────────────────────────────
 * Styles — inline; index.css belongs to another unit in this build.
 * ────────────────────────────────────────────────────────────────────────── */

const S = {
  page: { padding: 12, paddingBottom: 88 } as const,
  bar: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 } as const,
  chip: (on: boolean) => ({
    minHeight: 40, padding: '0 12px', borderRadius: 999, fontSize: 14, fontWeight: 600,
    border: `1px solid ${on ? 'var(--brand-strong)' : 'var(--line)'}`,
    background: on ? 'var(--brand-soft)' : 'var(--surface)', color: 'var(--ink)', cursor: 'pointer',
  }) as const,
  note: { fontSize: 13, color: 'var(--muted)', marginTop: 8 } as const,
  banner: {
    padding: '10px 12px', borderRadius: 'var(--radius)', fontSize: 14, marginBottom: 10,
    background: 'var(--warn-bg)', color: 'var(--warn)',
  } as const,
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

export default function MapScreen() {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const [locations, setLocations] = useState<LocationRow[]>([])
  const [listings, setListings] = useState<ListingRow[]>([])
  const [places, setPlaces] = useState<PlaceRow[]>([])
  const [layers, setLayers] = useState<Record<LayerId, boolean>>({
    listings: true, destination: true, ferry: true, transport: false,
  })
  const [radiusKm, setRadiusKm] = useState(0)
  const [view, setView] = useState<{ center: LatLng; zoom: number; key: string }>({
    center: ISLAND, zoom: ISLAND_ZOOM, key: 'island',
  })
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        // `places` is optional — the module is on in this project's config, but a
        // 404 from it must not take the listings layer down with it.
        const [loc, list, plc] = await Promise.all([
          get<{ locations: LocationRow[] }>('/api/locations'),
          get<{ listings: ListingRow[] }>('/api/listings'),
          get<{ places: PlaceRow[] }>('/api/places').catch(() => ({ places: [] as PlaceRow[] })),
        ])
        if (cancelled) return
        setLocations(loc.locations ?? [])
        setListings(list.listings ?? [])
        setPlaces(plc.places ?? [])
      } catch {
        if (!cancelled) setFailed(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  /** `location_id` → the opportunity a tap should open. */
  const opportunityByLocation = useMemo(() => {
    const map = new Map<string, string>()
    for (const listing of listings) {
      if (listing.location_id && listing.opportunity_id) map.set(listing.location_id, listing.opportunity_id)
    }
    return map
  }, [listings])

  const pins = useMemo<Pin[]>(() => {
    const out: Pin[] = []
    for (const location of locations) {
      if (typeof location.lat !== 'number' || typeof location.lng !== 'number') continue
      const layer = (Object.keys(LOCATION_ROLES) as LayerId[])
        .find((id) => LOCATION_ROLES[id].includes(location.role))
      if (!layer || !layers[layer]) continue
      out.push({
        id: `L:${location.id}`,
        lat: location.lat,
        lng: location.lng,
        label: location.label,
        layer,
        opportunityId: opportunityByLocation.get(location.id) ?? null,
      })
    }
    for (const place of places) {
      const layer = (Object.keys(PLACE_KINDS) as LayerId[])
        .find((id) => PLACE_KINDS[id].includes(place.kind))
      if (!layer || !layers[layer]) continue
      out.push({ id: `P:${place.id}`, lat: place.lat, lng: place.lng, label: place.name, layer })
    }
    return out
  }, [locations, places, layers, opportunityByLocation])

  const clustered = pins.length > CLUSTER_ABOVE
  const markers = useMemo<MapMarker[]>(
    () => (clustered ? clusterPins(pins, view.zoom) : pins.map(({ id, lat, lng, label }) => ({ id, lat, lng, label }))),
    [clustered, pins, view.zoom],
  )

  /** The ring sits on the land when we know where it is, and on the view otherwise. */
  const ringCenter = useMemo<LatLng>(() => {
    const land = locations.find((l) => l.role === 'destination_land' && typeof l.lat === 'number' && typeof l.lng === 'number')
    return land && typeof land.lat === 'number' && typeof land.lng === 'number'
      ? { lat: land.lat, lng: land.lng }
      : view.center
  }, [locations, view.center])

  const circles = useMemo<MapCircle[]>(
    // No explicit colour: Map.tsx falls back to `currentColor`, which resolves
    // against the map container and is therefore theme-aware. A `var(--token)`
    // passed here would land in a presentation attribute Leaflet sets with
    // setAttribute, where custom-property resolution is not something to bet a
    // visible feature on.
    () => (radiusKm > 0 ? [{ center: ringCenter, radiusKm }] : []),
    [radiusKm, ringCenter],
  )

  const onMarkerClick = useCallback((id: string) => {
    if (id.startsWith('C:')) {
      const cluster = markers.find((m) => m.id === id)
      if (!cluster) return
      // Two levels, not one: a single level on a dense cell usually re-clusters
      // into one badge in the same place, which reads as a dead tap.
      setView({ center: { lat: cluster.lat, lng: cluster.lng }, zoom: Math.min(MAX_ZOOM, view.zoom + 2), key: id })
      return
    }
    const pin = pins.find((p) => p.id === id)
    if (pin?.opportunityId) navigate(`/o/${pin.opportunityId}`)
  }, [markers, pins, view.zoom])

  const toggle = useCallback((id: LayerId) => {
    setLayers((current) => ({ ...current, [id]: !current[id] }))
  }, [])

  return (
    <div style={S.page}>
      {failed && <div style={S.banner}>{t('failed')}</div>}

      <div style={S.bar}>
        {(['listings', 'destination', 'ferry', 'transport'] as LayerId[]).map((id) => (
          <button key={id} type="button" style={S.chip(layers[id])} aria-pressed={layers[id]} onClick={() => toggle(id)}>
            {t(id)}
          </button>
        ))}
      </div>

      <div style={S.bar}>
        <span style={{ ...S.note, marginTop: 10, marginRight: 4 }}>{t('radius')}</span>
        {RADII_KM.map((km) => (
          <button key={km} type="button" style={S.chip(radiusKm === km)} aria-pressed={radiusKm === km} onClick={() => setRadiusKm(km)}>
            {km === 0 ? t('radiusOff') : `${km} km`}
          </button>
        ))}
        {view.key !== 'island' && (
          <button
            type="button"
            style={S.chip(false)}
            onClick={() => setView({ center: ISLAND, zoom: ISLAND_ZOOM, key: 'island' })}
          >
            {t('reset')}
          </button>
        )}
      </div>

      <MapView
        // The remount that actually moves the camera. See the header.
        key={`${view.key}:${view.zoom}`}
        markers={markers}
        circles={circles}
        center={view.center}
        zoom={view.zoom}
        // Off, deliberately: this screen decides where the camera is, and a
        // refit on every layer toggle would fight the tap that just zoomed in.
        fitToMarkers={false}
        height="min(62vh, 560px)"
        onMarkerClick={onMarkerClick}
        // OpenStreetMap's own tiles. Private, non-commercial, three users —
        // within the OSMF policy, and recorded in docs/DECISIONS.md. Changing
        // provider means changing `img-src` in services/api/src/http.ts too.
        {...OSM_DEV_TILES}
      />

      <p style={S.note}>
        {loading ? t('loading') : `${pins.length} ${t('pins')}`}
        {clustered ? ` — ${t('clustered')}` : ''}
        {!loading && pins.length === 0 ? ` ${t('empty')}` : ''}
      </p>
    </div>
  )
}
